import { useCallback, useEffect, useRef, useState } from 'react'
import { ACTIVE_PERSONA_SCENARIOS } from './personaScenarios'
import type { PersonaScenario, PlaybackState } from './simulatorTypes'

type UsePersonaPlayback = {
  state: PlaybackState
  activePersona: PersonaScenario
  // Stable per-persona key the host uses to re-key SimulatorEngine / the chat
  // source so switching personas resets the conversation.
  playbackKey: string
  previous: () => void
  next: () => void
  complete: () => void
}

// Pure UI state machine for the guided persona playback (AI-42). Owns only the
// current index and the Previous/Next/Complete transitions — no data
// fetching, no dispatch. `complete()` fires the optional onComplete callback so
// the wizard shell can advance once the merchant finishes the final persona.
// Reads ACTIVE_PERSONA_SCENARIOS (not PERSONA_SCENARIOS) — scenario 3 is
// intentionally hidden pending a replacement (see personaScenarios.ts) — so
// `total` / the progress dots / the "Scenario x/y" chip all shrink for free.
export const usePersonaPlayback = (
  onComplete?: () => void
): UsePersonaPlayback => {
  const total = ACTIVE_PERSONA_SCENARIOS.length
  const [index, setIndex] = useState(0)

  const previous = useCallback(() => {
    setIndex(i => Math.max(0, i - 1))
  }, [])

  const next = useCallback(() => {
    setIndex(i => Math.min(total - 1, i + 1))
  }, [total])

  // Keep `complete` referentially STABLE regardless of `onComplete`'s identity.
  // Callers (WP4's wizard) commonly pass an inline arrow, which would otherwise
  // churn `complete` every render and cascade through PlaybackNav's `onComplete`
  // prop. Stash the latest callback in a ref so `complete` keeps an empty dep
  // array while still invoking the current `onComplete`.
  const onCompleteRef = useRef(onComplete)
  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  const complete = useCallback(() => {
    onCompleteRef.current?.()
  }, [])

  const activePersona = ACTIVE_PERSONA_SCENARIOS[index]
  const state: PlaybackState = {
    index,
    total,
    isFirst: index === 0,
    isLast: index === total - 1,
  }

  return {
    state,
    activePersona,
    playbackKey: activePersona.persona_id,
    previous,
    next,
    complete,
  }
}
